import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  CheckSquare,
  MessageSquare,
  FileText,
  Users,
  Lock,
  X,
  TrendingUp,
  Building2,
  DollarSign,
  Package,
  Video,
  Film,
  Target,
  TrendingUp as TrendingUpIcon,
  Share2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  AppWindow,
  Settings
} from 'lucide-react';

const Sidebar = ({ isOpen = false, setIsOpen = () => {}, isCollapsed = false, setIsCollapsed = () => {} }) => {
  const location = useLocation();

  // Real Estate Managing Partner Menu Structure
  const menuItems = [
    // WORKSPACE Category
    { icon: null, label: 'WORKSPACE', path: null, isCategory: true },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: MessageSquare, label: 'Communications', path: '/communications-re' },
    { icon: Users, label: 'Contacts', path: '/contacts-re' },
    { icon: Calendar, label: 'Calendar', path: '/calendar' },
    { icon: FileText, label: 'Documents', path: '/documents-re' },
    { icon: Video, label: 'Meetings', path: '/meetings-re' },
    { icon: Package, label: 'Travel', path: '/travel' },
    
    // CRM Category
    { icon: null, label: 'CRM', path: null, isCategory: true },
    { icon: Target, label: 'Lead Generation', path: '/lead-generation' },
    { icon: Users, label: 'Clients & Customers', path: '/clients-customers-re' },
    { icon: Building2, label: 'Properties', path: '/properties' },
    
    // FILES Category
    { icon: null, label: 'FILES', path: null, isCategory: true },
    { icon: Film, label: 'Media Portfolio', path: '/media-portfolio-re' },
    { icon: Clapperboard, label: 'Media Factory', path: '/media-factory' },
    
    // TEAM Category
    { icon: null, label: 'TEAM', path: null, isCategory: true },
    { icon: Building2, label: 'Departments', path: '/departments-re' },
    { icon: Users, label: 'Employees', path: '/employees-re' },
    
    // MARKETING Category
    { icon: null, label: 'MARKETING', path: null, isCategory: true },
    { icon: Share2, label: 'Marketing', path: '/marketing-re' },
    { icon: Share2, label: 'Social Media', path: '/social-media' },
    
    // ACCOUNTING Category
    { icon: null, label: 'ACCOUNTING', path: null, isCategory: true },
    { icon: DollarSign, label: 'Finances', path: '/finances-re' },
    { icon: TrendingUpIcon, label: 'Investments', path: '/investments-re' },
    
    // UTILITIES Category
    { icon: null, label: 'UTILITIES', path: null, isCategory: true },
    { icon: Lock, label: 'Passwords', path: '/passwords' },
    { icon: AppWindow, label: 'My Apps', path: '/my-apps' }
  ];

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    const normalizedPath = location.pathname.replace(/\/$/, '');
    const normalizedCheckPath = path.replace(/\/$/, '');
    return normalizedPath === normalizedCheckPath || normalizedPath.startsWith(normalizedCheckPath + '/');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-transparent z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`h-screen fixed left-0 top-0 overflow-y-auto z-[60] transition-all duration-300 scrollbar-hide bg-[#000000] ${
        isCollapsed ? 'w-20' : 'w-64'
      } ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0`}>
        
        {/* Close button for mobile - At the very top */}
        <div className="md:hidden flex justify-end p-4">
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-white hover:text-gray-300 focus:outline-none transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Logo - Below close button on mobile */}
        <div className={`pb-4 px-4 ${isCollapsed ? 'px-2' : ''} ${isOpen ? 'pt-0' : 'pt-6'} md:pt-6`}>
          <Link to="/" className="flex items-center justify-center">
            {isCollapsed ? (
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black font-bold text-xl">
                D
              </div>
            ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_dashboard-hub-39/artifacts/cs5395nz_Blue%20and%20White%20Modern%20Medical%20Report%20Dashboard%20%286%29.png"
                alt="DW Logo"
                className="w-full h-auto max-w-[200px]"
              />
            )}
          </Link>
        </div>

        {/* Menu */}
        <div className="pt-8 pb-24">
          <div className="space-y-1">
            {menuItems.map((item, idx) => (
              item.isCategory ? (
                // Render category/section header - first category has less top margin
                <div
                  key={idx}
                  className={`flex items-center gap-3 ${
                    isCollapsed ? 'justify-center px-3 py-2 mx-2' : 'px-6 py-2 mx-3'
                  } text-gray-500 ${idx === 0 ? 'mt-0' : 'mt-8 pt-6'}`}
                >
                  {!isCollapsed && (
                    <span className="text-xs font-semibold tracking-wider uppercase">{item.label}</span>
                  )}
                </div>
              ) : (
                // Render regular menu item - bright white text
                <Link
                  key={idx}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 ${
                    isCollapsed ? 'justify-center px-3 py-3 mx-2' : 'px-6 py-3 mx-3'
                  } rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon && <item.icon size={20} />}
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </Link>
              )
            ))}
          </div>
          
          {/* Collapse Toggle Button - Desktop Only - Positioned below menu items */}
          <div className="mt-8 mb-4 px-3">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`hidden md:flex w-full items-center gap-2 px-4 py-3 bg-white rounded-xl text-black hover:bg-gray-100 transition-colors ${
                isCollapsed ? 'justify-center' : ''
              }`}
            >
              {isCollapsed ? (
                <ChevronRight size={18} className="text-black" />
              ) : (
                <>
                  <ChevronLeft size={18} className="text-black" />
                  <span className="text-sm font-medium">Collapse</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
